/**
 * La SCOPA dal BFF di Limulo: le sei rotte di qlash-limulo#245 (`/v1/games/scopa/*`), iniettate in `limuloApi` come `services/limuloLeghe.ts` (stessa base, stessa
 * sessione, stesso refresh), e NON dentro `services/limuloApi.ts`: la zona della scopa resta fuori dal file di tutti. La forma delle risposte e in `lib/gamehost/rest.ts`
 * e `lib/gamehost/ticket.ts` (il trasporto comune), le rotte e i corpi del gioco in `lib/scopa/gioco.ts`; i consumatori sono la Hub della scopa (`components/scopa/*`) e l'hook della partita (`hooks/useScopaMatch.ts`).
 *
 * 🔴 Le POST non si ritentano da sole (`limuloBaseQuery` non ritenta nessuna POST) e non si mettono in cache; le letture non restano in cache oltre la schermata
 * (`keepUnusedDataFor: 0`): un tavolo finito non deve ricomparire dalla cache.
 */
import { limuloApi } from '@/services/limuloApi';
import { corpoDi, entraBody, ingressoOf, mescolataOf, rivincitaBody, tavoliMieiOf, tavoloCreatoOf } from '@/lib/gamehost/rest';
import type { TavoliMiei, TavoloCreato } from '@/lib/gamehost/rest';
import { ticketBody, ticketGrantOf } from '@/lib/gamehost/ticket';
import type { TicketGrant } from '@/lib/gamehost/ticket';
import { creaTavoloBody, POSTI_AMMESSI, SCOPA_REST, SCOPA_TICKET_ROUTE } from '@/lib/scopa/gioco';
import type { DatiTavolo } from '@/lib/scopa/gioco';

export const scopaApi = limuloApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `scopa.table.mine.list` — i tavoli VIVI di chi chiama («I miei tavoli / Riprendi»). */
    getMyScopaTables: builder.query<TavoliMiei, void>({
      query: () => ({ url: SCOPA_REST.tavoliMiei }),
      transformResponse: (response: unknown) => tavoliMieiOf(response, POSTI_AMMESSI),
      keepUnusedDataFor: 0,
    }),
    /**
     * `scopa.table.create` — «Gioca» (casual), «Invita un amico» (privato) e la partita contro i robot (casual + `livello`): 2 posti la scopa, 4 lo scopone.
     * ⚠️ Il campo `robot: { livello }` arriva al game host solo dopo il mirror 0.2.x di app-qscopa su Limulo staging (kb#432) e con `robotAllaNascita` acceso in QScopa: finche
     * il BFF lo scarta, il tavolo nasce senza robot.
     */
    createScopaTable: builder.mutation<TavoloCreato | null, DatiTavolo>({
      query: (a) => ({ url: SCOPA_REST.tavoli, method: 'POST', body: creaTavoloBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `scopa.table.join` — «Entra con un codice»: il posto lo sceglie il game host. */
    joinScopaTable: builder.mutation<{ tableId: string } | null, { tableId: string; invito: string; idempotencyKey: string }>({
      query: (a) => ({ url: SCOPA_REST.posti(a.tableId), method: 'POST', body: entraBody(a) }),
      transformResponse: (response: unknown, _meta, a) => ingressoOf(response, a.tableId),
    }),
    /** `scopa.table.rematch` — la rivincita di un tavolo FINITO: un tavolo nuovo (201) o quello gia aperto da un altro (200). */
    rematchScopaTable: builder.mutation<TavoloCreato | null, { tableId: string; idempotencyKey: string }>({
      query: (a) => ({ url: SCOPA_REST.rivincita(a.tableId), method: 'POST', body: rivincitaBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `scopa.table.mescolata.read` — il record di una smazzata finita (`qlash-fair-v1`). La verifica a schermo non e in questa PR: si legge e basta. */
    getScopaMescolata: builder.query<Record<string, unknown> | null, { tableId: string; smazzata: number }>({
      query: (a) => ({ url: SCOPA_REST.mescolata(a.tableId, a.smazzata) }),
      transformResponse: (response: unknown) => mescolataOf(response),
      keepUnusedDataFor: 0,
    }),
    /** `scopa.session.ticket.issue` — il ticket MONOUSO (30 s) del socket del tavolo: corpo `{ tableId }` (lo scopo lo compone la rotta del kit). Chi lo chiede lo consuma subito. */
    issueScopaTicket: builder.mutation<TicketGrant | null, { tableId: string }>({
      query: ({ tableId }) => ({ url: SCOPA_TICKET_ROUTE, method: 'POST', body: ticketBody(tableId) }),
      transformResponse: (response: unknown) => ticketGrantOf(corpoDi(response)),
    }),
  }),
});

export const {
  useGetMyScopaTablesQuery,
  useCreateScopaTableMutation,
  useJoinScopaTableMutation,
  useRematchScopaTableMutation,
  useLazyGetScopaMescolataQuery,
  useIssueScopaTicketMutation,
} = scopaApi;
